import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

const VideoContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  justify-content: center;
  align-items: center;
  /* 배경색 제거: AppLayout의 배경이 그대로 보이게 */
  background: transparent;
  
  @media (max-width: 768px) {
    width: 100vw;
    height: calc(var(--vh, 1vh) * 100);
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
`;

const VideoText = styled.div`
  color: white;
  font-size: 1rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
  margin-bottom: 20px;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 15px;
  }
`;

const PlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 370px;
  max-height: 700px;
  aspect-ratio: 9/16;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease-in-out, opacity 0.5s;
  
  &.minimized {
    position: absolute;
    top: 20px;
    right: 0;
    width: 115px;
    height: 200px;
    max-width: 120px;
    max-height: 200px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-radius: 8px;
    opacity: 0.4;
    aspect-ratio: 9/16;
  }
  
  @media (max-width: 768px) {
    /* max-width: 70vw; */
    aspect-ratio: 9 / 16;
    width: auto;
    max-height: 60vh;

    &.minimized {
      width: 115px;
      height: 200px;
      max-width: 115px;
      max-height: 200px;
      top: 0;
      right: 0;
    }
  }
`;

const TopRightOverlay = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 20;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const CountdownBox = styled.div`
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 0.95rem;
  font-weight: bold;
  border-radius: 12px;
  padding: 3px 10px;
  min-width: 28px;
  text-align: center;
`;

const SkipButton = styled.button`
  background: #0e805a;
  color: #e0e0e0;
  font-weight: 600;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 0.95rem;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s, border 0.15s;

  &:hover {
    opacity: 1;
    border: 1.5px solid #bdbdbd;
    color: #fff;
  }
`;

//max-height: 900px;

const VideoPlayerIframe = ({ onVideoEnd, videoUrl }) => {
  const endedRef = useRef(false);
  const [countdown, setCountdown] = useState(5);
  const [showSkip, setShowSkip] = useState(false);
  const [isVideoActuallyPlaying, setIsVideoActuallyPlaying] = useState(false);
  const [hasStartedCountdown, setHasStartedCountdown] = useState(false);
  const countdownTimer = useRef(null);
  const skipHandled = useRef(false);
  const playerRef = useRef(null);
  const progressTimer = useRef(null);
  const youtubePlayer = useRef(null);

  // YouTube iframe URL 생성 함수
  const generateYouTubeUrl = () => {
    const urlObj = new URL(videoUrl);
    let videoId = urlObj.searchParams.get('v');
    // Shorts URL 지원
    if (!videoId) {
      const paths = urlObj.pathname.split('/');
      videoId = paths[paths.length - 1];
    }
    
    const params = new URLSearchParams({
      autoplay: '0', // 자동재생 끄기
      mute: '0', // 소리 켜기
      controls: '1', // 컨트롤 표시 (0: 비활성화, 1: 활성화)
      disablekb: '1', // 키보드 컨트롤 허용
      fs: '0',
      iv_load_policy: '3',
      modestbranding: '1',
      rel: '0',
      showinfo: '0',
      playsinline: '1',
      enablejsapi: '1', // YouTube API 활성화
      origin: window.location.origin,
      widget_referrer: window.location.origin,
      cc_load_policy: '0',
      color: 'white',
      hl: 'ko',
      loop: '0', // 루프 비활성화
      volume: '100'
    });

    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  };

  // 영상 진행 상태 체크 함수
  const checkVideoProgress = () => {
    if (youtubePlayer.current && typeof youtubePlayer.current.getCurrentTime === 'function') {
      try {
        const currentTime = youtubePlayer.current.getCurrentTime();
        const duration = youtubePlayer.current.getDuration();
        
        // 영상이 실제로 재생되고 있는지 확인
        if (!isVideoActuallyPlaying && currentTime > 0 && duration > 0) {
          setIsVideoActuallyPlaying(true);
        }
        
        // 영상이 끝나기 1초 전에 자동으로 종료
        if (!skipHandled.current && duration && (duration - currentTime <= 2)) {
          handleSkip();
        }
      } catch (error) {
        //console.log('Error checking video progress:', error);
      }
    }
  };

  //1. 카운트다운 시작 트리거
  useEffect(() => {
    if (isVideoActuallyPlaying && !hasStartedCountdown) {
      setHasStartedCountdown(true);
      setCountdown(5);
      setShowSkip(false);
      skipHandled.current = false;
    }
  }, [isVideoActuallyPlaying, hasStartedCountdown]);

  // 2. 카운트다운 타이머
  useEffect(() => {
    if (!hasStartedCountdown) return;
    if (countdown === 0) {
      setShowSkip(true);
      return;
    }
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasStartedCountdown, countdown]);
 

  // 스킵 버튼 클릭 또는 영상 끝나면 비디오 종료
  const handleSkip = () => {
    if (skipHandled.current) return;
    skipHandled.current = true;
    //console.log('카운트다운 완료 - 영상 종료');
    // 영상을 숨기고 종료 콜백 호출
    setIsVideoActuallyPlaying(false);
    if (onVideoEnd) {
      //console.log('onVideoEnd 호출');
      onVideoEnd();
    }
  };

  const handleEnded = () => {
    if (skipHandled.current) return;
    skipHandled.current = true;
    // 영상을 숨기고 종료 콜백 호출
    setIsVideoActuallyPlaying(false);
    onVideoEnd && onVideoEnd();
  };

  // // 비디오 URL이 변경되면 상태 리셋
  useEffect(() => {
    setIsVideoActuallyPlaying(false);
    setHasStartedCountdown(false);
    setCountdown(5);
    setShowSkip(false);
    skipHandled.current = false;
    endedRef.current = false;
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }
    if (progressTimer.current) {
      clearInterval(progressTimer.current);
    }
  }, [videoUrl]);

  // YouTube iframe API 초기화 및 재생 상태 감지
  useEffect(() => {
    if (!videoUrl) return;

    // YouTube iframe API 로드
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // YouTube API 준비되면 플레이어 초기화
    const onYouTubeIframeAPIReady = () => {
      if (window.YT) {
        youtubePlayer.current = new window.YT.Player('youtube-player', {
          events: {
            'onStateChange': onPlayerStateChange
          }
        });
        
        // 영상 진행 상태를 주기적으로 체크 (1초마다)
        progressTimer.current = setInterval(checkVideoProgress, 1000);
      }
    };

    // 플레이어 상태 변경 감지
    const onPlayerStateChange = (event) => {
      //console.log('YouTube player state changed:', event.data);
      // 재생 시작 (state 1: playing)
      if (event.data === 1) {
        setIsVideoActuallyPlaying(true);
      }
      // 일시정지 (state 2: paused)
      else if (event.data === 2) {
        //console.log('Video paused');
      }
      // 재생 종료 (state 0: ended)
      else if (event.data === 0) {
        //console.log('Video ended');
        handleEnded();
      }
    };

    // YouTube API가 이미 로드되어 있으면 바로 초기화
    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      // YouTube API 로드 대기
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }

    return () => {
      // 정리
      if (window.onYouTubeIframeAPIReady === onYouTubeIframeAPIReady) {
        delete window.onYouTubeIframeAPIReady;
      }
      if (progressTimer.current) {
        clearInterval(progressTimer.current);
      }
    };
  }, [videoUrl]);

  return (
    <VideoContainer style={{minHeight: 0, height: 'auto', flex: 1}}>
      <VideoText>* 영상 재생 5초 후 게임을 시작할 수 있습니다.</VideoText>
      <PlayerWrapper>
        {/* 오른쪽 상단 카운트다운 - 실제 재생 중일 때만 표시 */}
        {isVideoActuallyPlaying && hasStartedCountdown && (
          <TopRightOverlay>
            {countdown > 0 && <CountdownBox>{countdown}</CountdownBox>}
            {showSkip && <SkipButton onClick={handleSkip}>SKIP</SkipButton>}
          </TopRightOverlay>
        )}
        <iframe
          ref={playerRef}
          id="youtube-player"
          src={generateYouTubeUrl()}
          width="100%"
          height="100%"
          frameBorder="0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen={false}
          style={{
            objectFit: 'cover',
            borderRadius: '12px',
            boxSizing: 'border-box'
          }}
          onLoad={() => {
            //console.log('Video iframe loaded');
          }}
        />
      </PlayerWrapper>
    </VideoContainer>
  );
};

export default VideoPlayerIframe; 